'use client'
import { productCardHelper } from '@/components/common/product-card/helper'
import { TrackingDataLayer } from '@/components/extension/tracking-data-layer'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useSearchProductsQuery } from '@/hooks/react-query/products/use-search-products'
import { useMobileNav } from '@/hooks/zustands/use-mobile-nav'
import { useSearchProductsStore } from '@/hooks/zustands/use-search-products'
import { ETrackingEvent } from '@/lib/gtm/type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, formatPrice } from '@/lib/utils'
import { useSettingData } from '@/providers/setting-provider'
import { Search, SearchIcon, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import { useBoolean, useDebounceValue } from 'usehooks-ts'
import { RecentProducts } from './recent-products'

export function SearchBar({ isMobile }: { isMobile?: boolean }) {
  const homeSetting = useSettingData()

  const { value: open, setTrue, setFalse } = useBoolean(false)

  const { keyword, setKeyword } = useSearchProductsStore()
  const [searchTerm, setSearchTerm] = useDebounceValue(keyword, 500)

  const inputRef = useRef<HTMLInputElement>(null)
  const dialogContentRef = useRef<HTMLDivElement>(null)

  const isSearching = searchTerm.trim().length > 0

  const popularCategories = homeSetting?.keyword?.split(',') ?? []

  const handleCategoryClick = (category: string) => {
    setSearchTerm(category)
    setKeyword(category)
    // Focus input after setting value
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  //TODO: COmment tạm đang có bug click vào clear cũng bị close

  // const handleOverlayClick = (e: React.MouseEvent) => {
  //   // Close dialog when clicking outside the content area
  //   if (!dialogContentRef.current?.contains(e.target as Node)) {
  //     setFalse()
  //   }
  // }

  return (
    <>
      <div onClick={setTrue}>
        {isMobile ? (
          <span className='flex h-13 w-10.5 cursor-pointer items-center justify-center'>
            <Search size={20} />
          </span>
        ) : (
          <div className='relative cursor-pointer'>
            <Input
              ref={inputRef}
              value={searchTerm}
              placeholder='Tìm kiếm sản phẩm...'
              className='h-12 cursor-pointer rounded-full bg-white py-2 pr-4'
              readOnly
            />
            <SearchIcon className='absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-500' />
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setFalse}>
        <DialogContent
          showCloseButton={false}
          className='top-0 flex max-w-full translate-y-0 flex-col gap-2 bg-transparent p-0'
          // onClick={handleOverlayClick}
        >
          <DialogTitle className='hidden'></DialogTitle>
          <div className='flex items-center justify-center gap-4 bg-white p-4 md:gap-8'>
            <div className='relative max-w-96 flex-1'>
              <Input
                ref={inputRef}
                type='search'
                value={keyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchTerm(e.target.value)
                  setKeyword(e.target.value)
                }}
                placeholder='Tìm kiếm sản phẩm...'
                className='h-12 appearance-none rounded-full bg-gray-100 pl-10 shadow-none focus-visible:ring-0 [&::-webkit-search-decoration]:appearance-none'
                autoFocus
              />
              <SearchIcon className='absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-500' />
            </div>
            <button onClick={setFalse}>
              <X className='h-6 w-6 text-gray-500 md:h-8 md:w-8' />
            </button>
          </div>

          {/* Content with Side Margins */}
          <div className='h-full border-none'>
            <div
              ref={dialogContentRef}
              className='mx-auto max-w-[calc(100%-20px)] overflow-hidden rounded-lg bg-white p-6 md:px-20 md:py-10 lg:max-w-5xl'
            >
              {/* Categories */}
              {!isSearching ? (
                <div className='mb-4'>
                  <p className='mb-4 text-base font-medium'>
                    Từ khóa nổi bật ngày hôm nay
                  </p>
                  <div className='flex flex-nowrap gap-2 overflow-x-auto pb-3'>
                    {popularCategories.map((category) => (
                      <Button
                        key={category}
                        className={cn(
                          'rounded-full border border-neutral-200 px-4 py-2 font-sans text-sm normal-case',
                          keyword === category && 'bg-neutral-900 text-neutral'
                        )}
                        variant='outline-neutral'
                        size='small'
                        onClick={() => handleCategoryClick(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}

              {isSearching ? (
                <SearchResultProducts
                  keyword={searchTerm}
                  setFalse={setFalse}
                />
              ) : (
                <RecentProducts setFalse={setFalse} />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const SearchResultProducts = ({
  keyword,
  setFalse,
}: {
  keyword: string
  setFalse: () => void
}) => {
  const { setIsOpen } = useMobileNav()

  const {
    data: searchProducts,
    isLoading,
    isError,
  } = useSearchProductsQuery(keyword)

  if (isLoading) {
    return (
      <div className='grid auto-cols-[160px] grid-flow-col gap-4 overflow-x-auto md:auto-cols-fr md:grid-cols-3 lg:grid-cols-4'>
        {[...Array(4)].map((_, i) => (
          <div key={i} className='space-y-4'>
            <Skeleton className='aspect-[3/4] rounded-lg' />
            <Skeleton className='h-4 w-2/3' />
            <Skeleton className='h-4 w-1/2' />
          </div>
        ))}
      </div>
    )
  }

  if (isError || searchProducts?.products?.length === 0 || !searchProducts) {
    return (
      <>
        <div className='text-center text-sm italic text-neutral-700'>
          Không có kết quả
        </div>
        <TrackingDataLayer
          data={{
            event: ETrackingEvent.SearchNoResults,
            search_term: keyword,
          }}
        />
      </>
    )
  }

  return (
    <div>
      <h3 className='mb-4 text-base font-medium'>Kết quả tìm kiếm</h3>
      <div className='grid auto-cols-[160px] grid-flow-col gap-4 overflow-x-auto md:auto-cols-fr md:grid-cols-3 lg:grid-cols-4'>
        {searchProducts?.products?.map((product) => {
          const { regular_price, compare_price, sale_percent } =
            productCardHelper.getPrices(product)
          return (
            <Link
              href={`/product/${product.seo_alias}`}
              key={product.seo_alias}
              onClick={() => {
                setTimeout(() => {
                  setFalse()
                  setIsOpen(false)
                }, 300)
              }}
            >
              <div className='space-y-2'>
                <div className='relative aspect-[3/4] overflow-hidden rounded-lg bg-gray-50'>
                  <Image
                    src={ImageHelper.getImageUrl(
                      product.images?.[0].src || '/placeholder.jpg',
                      's'
                    )}
                    alt={product.title}
                    className='h-full w-full object-cover'
                    width={300}
                    height={300}
                  />
                  {!product?.icon_thumbnail?.hide &&
                    product?.icon_thumbnail?.image && (
                      <div className='absolute bottom-0 left-0 z-[1] h-auto w-full rounded-b-lg'>
                        <Image
                          src={ImageHelper.getImageUrl(
                            product?.icon_thumbnail?.image,
                            '585_663'
                          )}
                          width={433}
                          height={62}
                          alt={`${product.title} thumbnail image`}
                          className='block h-auto w-full rounded-b-lg object-cover'
                          loading='lazy'
                        />
                      </div>
                    )}
                </div>
                <p className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 hover:text-primary lg:text-base lg:leading-5'>
                  {product.title}
                </p>
                <div className='flex items-center gap-1'>
                  <span
                    className={
                      'font-sans text-xs font-bold leading-3.5 text-neutral-900 lg:text-base lg:leading-5'
                    }
                  >
                    {formatPrice(regular_price ?? 0)}
                  </span>
                  {compare_price && compare_price !== regular_price ? (
                    <span className='text-xs leading-3.5 text-neutral-400 line-through lg:text-sm lg:leading-4.5'>
                      {formatPrice(compare_price ?? 0)}
                    </span>
                  ) : null}
                  {sale_percent ? (
                    <TagThumbnail
                      title={`-${sale_percent}%`}
                      variant='primary'
                      size={'small'}
                      className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
                    />
                  ) : null}
                </div>
              </div>
            </Link>
          )
        })}
      </div>
      {/* Loadmore button */}
      {
        <div className='mt-4 flex justify-center md:mt-8'>
          <Link
            href={`/spotlight?search=${keyword}`}
            onClick={() => {
              setFalse()
              setTimeout(() => {
                setIsOpen(false)
              }, 300)
            }}
          >
            <Button variant='black'>Xem thêm</Button>
          </Link>
        </div>
      }
    </div>
  )
}
